import {z} from 'zod';
import {resourceLlmCallResponseSchema} from '../resourceKnowledgeTool.schemas';
import {FCResource} from '../resourceKnowledgeTool.types';
import openAISystemMessage from '../../../openAI/utils/openAISystemMessage';
import openAIUserMessage from '../../../openAI/utils/openAIUserMessage';
import invokeStandaloneLLMCall from '../../../utils/invokeStandaloneLLMCall';

async function askAboutResources({
  resourcesWithTempIds,
  formatter,
  additionalInstructions,
  prompt,
  abortSignal,
}: {
  resourcesWithTempIds: {tempId: string; resource: FCResource}[];
  formatter: (resource: FCResource) => string;
  additionalInstructions?: string;
  prompt: string;
  abortSignal: AbortSignal;
}): Promise<z.infer<typeof resourceLlmCallResponseSchema>> {
  const instructions = `You are an expert on the resources of the Flight Control product.

### Strict rules:

1. Answer **only based on the provided resources below**. Do **not** use your own knowledge.
2. NEVER mention "resources" in your answer - this is an internal term.
3. If the question is vague or unclear, ask the user to clarify.

Respond **only** using the exact language and facts found in the resources. **Never go beyond it under any circumstances.**

### Instructions regarding which relevant temp IDs to return:

1. If you didn't find an answer to the user's question, but the resource's name is relevant to the question, include its temp ID in the list.
2. If the resource's name doesn't seem to be the one that the user is actually asking about, don't include its temp ID in the list.

${
  additionalInstructions
    ? `### Resource-type specific rules:

${additionalInstructions}
`
    : ''
}
  
## Resources
  
${resourcesWithTempIds
  .map(
    (resourceWithTempId) =>
      `Temp ID: ${resourceWithTempId.tempId}
${formatter(resourceWithTempId.resource)}`,
  )
  .join('\n\n')}
`;

  const messages = [openAISystemMessage(instructions), openAIUserMessage(prompt)];

  const responseObject = await invokeStandaloneLLMCall({
    messages,
    responseSchema: resourceLlmCallResponseSchema,
    abortSignal,
  });

  return responseObject;
}

export default askAboutResources;
